import React, { useId, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { styled } from '@compiled/react';
// We have deprecated unstated. Please use react-sweet-state instead
import { Subscribe } from 'unstated';

import { token } from '@atlaskit/tokens';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import Box from '@atlaskit/primitives/box';
// oxlint-disable-next-line @atlaskit/design-system/no-emotion-primitives
import { xcss } from '@atlaskit/primitives/xcss';

import { ScreenEvent } from '@confluence/analytics/entry-points/ScreenEvent.ts';
import { DialogsStateContainer } from '@confluence/dialogs/entry-points/DialogsStateContainer.ts';

import { ArchiveIcon } from './img/ArchiveIcon.js';
import { ArchivedPageBannerNote, ArchivePageActionLink } from './ArchivedPageBannerNote/index.ts';
import {
	i18n,
	ARCHIVED_PAGE_BANNER_ANALYTICS_SOURCE,
	ARCHIVED_PAGE_BANNER_RESTORE_LINK_TESTID,
} from './archivingConstants.ts';

const ARCHIVE_PAGES_DOCS_LINK = 'https://support.atlassian.com/confluence-cloud/docs/archive-pages';

const BannerWrapperCommonStyles = xcss({
	display: 'flex',
	alignItems: 'flex-start',
	backgroundColor: 'color.background.neutral.bold',
	borderRadius: 'radius.large',
	color: 'color.text.inverse',
	minWidth: '450px',
	width: '100%',
	paddingBlock: 'space.200',
	paddingInline: 'space.300',
	boxSizing: 'border-box',
	marginBottom: 'space.0',
});
const marginBottomStyles = xcss({ marginBottom: 'space.200' });
const marginTopStyles = xcss({ marginTop: 'space.200' });
const marginBothStyles = xcss({ marginTop: 'space.200', marginBottom: 'space.200' });

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const BannerTextWrapper = styled.div({
	flexGrow: 1,
	marginRight: token('space.300'),
});

// The icon must have a wrapping <div> since AK icons don't accept a `padding` prop
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const IconWrapper = styled.div({
	marginRight: token('space.250'),
	display: 'flex',
	flexShrink: 0,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const BannerTitleText = styled.div({
	font: token('font.body'),
	fontWeight: token('font.weight.medium'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const BannerActionText = styled.div({
	font: token('font.body'),
	flexShrink: 0,
	alignSelf: 'center',
});

type ArchivedPageBannerContentProps = {
	contentId: string;
	marginStyle: 'none' | 'bottom' | 'top' | 'both';
	userCanRestorePage: boolean;
	archiveNote: string;
	archiverAccountId: string | null | undefined;
	getUnarchiveDialogOpener: (dialogs: DialogsStateContainer) => () => void;
	fireLearnMoreEvent: () => void;
};

export const ArchivedPageBannerContent = ({
	contentId,
	marginStyle,
	userCanRestorePage,
	archiveNote,
	archiverAccountId,
	getUnarchiveDialogOpener,
	fireLearnMoreEvent,
}: ArchivedPageBannerContentProps): React.JSX.Element => {
	const [editingNote, setEditingNote] = useState(false);
	const titleId = useId();

	return (
		<Subscribe to={[DialogsStateContainer]}>
			{(dialogs: DialogsStateContainer) => (
				<>
					<ScreenEvent
						name={ARCHIVED_PAGE_BANNER_ANALYTICS_SOURCE}
						id="" // "id" is a required prop, but is not actually sent in the event
						attributes={{ canRestore: userCanRestorePage }}
					/>
					<Box
						// A named region so the banner is a navigable landmark; its visible title is the name
						role="region"
						aria-labelledby={titleId}
						xcss={[
							BannerWrapperCommonStyles,
							marginStyle === 'bottom' && marginBottomStyles,
							marginStyle === 'top' && marginTopStyles,
							marginStyle === 'both' && marginBothStyles,
						]}
					>
						<IconWrapper>
							<ArchiveIcon fill={token('color.icon.inverse')} />
							{/* Icon doesn't need an aria-label because the element's label tells aria what the banner is */}
						</IconWrapper>
						<BannerTextWrapper>
							<BannerTitleText id={titleId}>
								<FormattedMessage
									{...(userCanRestorePage
										? i18n.hasRestoreContentPermissionBannerMessage
										: i18n.noRestoreContentPermissionBannerMessage)}
								/>
							</BannerTitleText>
							<ArchivedPageBannerNote
								note={archiveNote}
								archiverAccountId={archiverAccountId}
								contentId={contentId}
								editingNote={editingNote}
								setEditingNote={setEditingNote}
							/>
						</BannerTextWrapper>
						{!editingNote && (
							<BannerActionText>
								{userCanRestorePage ? (
									<ArchivePageActionLink
										data-testid={ARCHIVED_PAGE_BANNER_RESTORE_LINK_TESTID}
										onClick={getUnarchiveDialogOpener(dialogs)}
									>
										<FormattedMessage {...i18n.restoreLinkMessage} />
									</ArchivePageActionLink>
								) : (
									<ArchivePageActionLink
										target="_blank"
										href={ARCHIVE_PAGES_DOCS_LINK}
										onClick={fireLearnMoreEvent}
									>
										<FormattedMessage {...i18n.learnMoreLinkMessage} />
									</ArchivePageActionLink>
								)}
							</BannerActionText>
						)}
					</Box>
				</>
			)}
		</Subscribe>
	);
};
